import { useEffect, useRef, useState } from "react";
import { Building2, Upload, Crown, Loader2, Save, Coins, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { company_name: "", cui: "", niche: "", county: "", city: "", phone: "", website: "", description: "", logo_url: "", directory_listed: true };

export const CompanyProfileEdit = () => {
  const { refreshUser, setTokens } = useAuth();
  const [form, setForm] = useState(empty);
  const [niches, setNiches] = useState([]);
  const [counties, setCounties] = useState([]);
  const [premium, setPremium] = useState({ is_premium: false, premium_until: null, tokens_balance: 0 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const fileRef = useRef(null);

  const loadProfile = () => {
    api.get("/company-profile").then((r) => {
      const d = r.data;
      setForm({ company_name: d.company_name, cui: d.cui, niche: d.niche, county: d.county, city: d.city, phone: d.phone, website: d.website, description: d.description, logo_url: d.logo_url || "", directory_listed: d.directory_listed });
      setPremium({ is_premium: d.is_premium, premium_until: d.premium_until, tokens_balance: d.tokens_balance });
    }).catch(() => {});
  };

  useEffect(() => {
    api.get("/company-niches").then((r) => setNiches(r.data.niches)).catch(() => {});
    api.get("/ro-locations").then((r) => setCounties(r.data.counties)).catch(() => {});
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.company_name.trim()) { toast.error("Numele companiei este obligatoriu."); return; }
    setSaving(true);
    try {
      await api.put("/company-profile", form);
      toast.success("Profil salvat. Apari acum în Cégjegyzék.");
      refreshUser();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/company-profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", r.data.url);
      toast.success("Logo încărcat.");
    } catch (e2) { toast.error(formatApiError(e2?.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const upgradePremium = async () => {
    setUpgrading(true);
    try {
      const r = await api.post("/company-profile/premium");
      setPremium((p) => ({ ...p, is_premium: true, premium_until: r.data.premium_until, tokens_balance: r.data.tokens_balance }));
      setTokens(r.data.tokens_balance);
      toast.success(`Premium activat! -${r.data.tokens_charged} tokenuri. Ești acum în topul jegyzék-ului.`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setUpgrading(false); }
  };

  const logoSrc = form.logo_url ? (form.logo_url.startsWith("http") ? form.logo_url : `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`) : "";

  return (
    <section className="mt-14" data-testid="company-profile-edit">
      <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2 flex items-center gap-2">
        <Building2 className="w-4 h-4" /> Profilul companiei — Cégjegyzék
      </h2>
      <p className="text-sm text-slate-500 mb-5 max-w-2xl">Completează profilul ca să apari în directorul public de companii verificate.</p>

      {/* Premium banner */}
      <div className={`border p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 ${premium.is_premium ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid="premium-banner">
        <Crown className={`w-8 h-8 shrink-0 ${premium.is_premium ? "text-amber-500" : "text-slate-400"}`} />
        <div className="flex-1">
          <div className="font-bold">{premium.is_premium ? "Premium Listing activ" : "Premium Listing"}</div>
          <div className="text-sm text-slate-500">
            {premium.is_premium
              ? `Ești în topul directorului până la ${new Date(premium.premium_until).toLocaleString("ro-RO")}.`
              : "Apari în vârful directorului, deasupra tuturor companiilor. 20 tokenuri / zi."}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700" data-testid="token-balance"><Coins className="w-4 h-4 text-amber-500" /> {premium.tokens_balance} tk</span>
          <button onClick={upgradePremium} disabled={upgrading}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 px-4 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-50"
            data-testid="upgrade-premium-btn">
            {upgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
            {premium.is_premium ? "Prelungește (20 tk)" : "Upgrade (20 tk/zi)"}
          </button>
        </div>
      </div>

      <div className="border border-slate-200 p-5 grid gap-4 sm:grid-cols-2" data-testid="company-edit-form">
        {/* Logo */}
        <div className="sm:col-span-2 flex items-center gap-4">
          <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center overflow-hidden shrink-0">
            {logoSrc ? <img src={logoSrc} alt="logo" className="w-full h-full object-cover" /> : <Building2 className="w-8 h-8" />}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-50" data-testid="upload-logo-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Încarcă logo
            </button>
            <p className="text-[11px] text-slate-400 mt-1">PNG/JPG, max 4 MB</p>
          </div>
        </div>

        <Field label="Nume companie *"><input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputCls} data-testid="cp-name" /></Field>
        <Field label="CUI / TVA"><input value={form.cui} onChange={(e) => set("cui", e.target.value)} className={inputCls} data-testid="cp-cui" /></Field>
        <Field label="Nișă industrială">
          <Select value={form.niche || "__none"} onValueChange={(v) => set("niche", v === "__none" ? "" : v)}>
            <SelectTrigger className="rounded-none bg-white" data-testid="cp-niche"><SelectValue placeholder="Alege nișa" /></SelectTrigger>
            <SelectContent className="max-h-[50vh]"><SelectItem value="__none">—</SelectItem>{niches.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Județ">
          <Select value={form.county || "__none"} onValueChange={(v) => set("county", v === "__none" ? "" : v)}>
            <SelectTrigger className="rounded-none bg-white" data-testid="cp-county"><SelectValue placeholder="Alege județul" /></SelectTrigger>
            <SelectContent className="max-h-[50vh]"><SelectItem value="__none">—</SelectItem>{counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Oraș"><input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="cp-city" /></Field>
        <Field label="Telefon"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="cp-phone" /></Field>
        <Field label="Website"><input value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} placeholder="https://" data-testid="cp-website" /></Field>
        <Field label="Descriere" full><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inputCls} data-testid="cp-description" /></Field>

        <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={form.directory_listed} onChange={(e) => set("directory_listed", e.target.checked)} data-testid="cp-listed" />
          <ShieldCheck className="w-4 h-4 text-emerald-600" /> Afișează compania în directorul public
        </label>

        <div className="sm:col-span-2">
          <button onClick={save} disabled={saving}
            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50" data-testid="cp-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salvează profilul
          </button>
        </div>
      </div>
    </section>
  );
};

const inputCls = "w-full mt-1 border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-orange-500";
const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-xs uppercase tracking-wide font-bold text-slate-500">{label}</label>
    {children}
  </div>
);
